import { useEffect, useState } from "react";
import {
  Button,
  Modal,
  TextAreaInput,
  TextInput,
} from "@monolith-forensics/monolith-ui";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import { useUpdateCaseFile } from "../files.query";
import { File } from "../files.types";
import { EvidenceFileModalAlert } from "@/Features/EvidenceFiles/EvidenceFileModalAlert";

export const EditCaseFileModal: React.FC<{
  show: boolean;
  onClose?: () => void;
  onSubmit?: (data: File) => void;
  onCancel?: () => void;
  defaultFormData: File;
  buttonProps?: {
    submitText?: string;
    cancelText?: string;
  };
}> = ({
  show,
  onClose,
  onSubmit,
  onCancel,
  buttonProps = {},
  defaultFormData,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState(defaultFormData.name);
  const [description, setDescription] = useState(defaultFormData.description);
  const [error, setError] = useState<string | null>(null);
  const { mutate: updateCaseFile } = useUpdateCaseFile();

  const isFolder = defaultFormData.is_folder;

  const resetForm = () => {
    setName(defaultFormData.name || "");
    setError(null);
  };

  const handleSubmit = async () => {
    if (!name) {
      setError("File name is required");
      return;
    }

    setError(null);

    setSubmitting(true);

    updateCaseFile(
      {
        uuid: defaultFormData.uuid,
        name: name,
        description: description || undefined,
      },
      {
        onSuccess: (data) => {
          setSubmitting(false);
          onSubmit?.(data.data);
        },
      }
    );
  };

  const handleCancel = () => {
    resetForm();
    onCancel?.();
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleCancel();
      }

      if (e.key === "Enter") {
        handleSubmit();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [show, name, description]);

  return (
    <Modal open={show} onClose={onClose}>
      <Modal.Title>Edit {isFolder ? "Folder" : "File"}</Modal.Title>
      <EvidenceFileModalAlert file={defaultFormData} kind="edit" />
      <div
        style={{
          fontWeight: 600,
          margin: "20px 0px",
          width: "350px",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        <TextInput
          value={name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setName(e.target.value);
          }}
          label={isFolder ? "Folder Name" : "File Name"}
          required={true}
          description={
            isFolder
              ? "The name of the folder to create."
              : "The name of the file to create."
          }
          placeholder={isFolder ? "Enter a folder name" : "Enter a file name"}
          error={error || undefined}
          // The evidence alert sits above the form, so without this its link
          // would get focus first (ENG-2309).
          autoFocus
        />
        <TextAreaInput
          value={description || ""}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
            setDescription(e.target.value);
          }}
          minRows={4}
          maxRows={6}
          label="Description"
          placeholder={`Enter a description for ${
            isFolder ? "folder" : "file"
          }`}
        />
      </div>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          {buttonProps?.cancelText || "Cancel"}
        </Button>
        <Button
          size="xs"
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        >
          {buttonProps?.submitText || "Submit"}
        </Button>
      </ButtonMenu>
    </Modal>
  );
};
